import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Tooltip } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { catchError, of } from 'rxjs';

import { ProjectTaskService, MyProjectTasks } from '../../services/project-task.service';
import { YearMonthFilterService } from '../../services/year-month-filter.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import { ProjectTasksComponent } from '../project-tasks/project-tasks.component';

/**
 * Project Task Tracker, as developers reach it from Work Log.
 *
 * The Projects module owns the same backlog, and this is a second window onto it rather than
 * a copy: both read and write the same ProjectTask records, so a task added here appears
 * there immediately, the way team allocation already works.
 *
 * What differs is reach. The server returns only the projects the signed-in developer is
 * allocated to that month — it takes no employee or project id — so this page needs neither
 * the Projects module nor the employee list to show someone their own work.
 */
@Component({
  selector: 'app-my-project-tasks',
  standalone: true,
  imports: [CommonModule, FormsModule, Tooltip, ProjectTasksComponent],
  templateUrl: './my-project-tasks.component.html',
  styleUrl: './my-project-tasks.component.scss',
})
export class MyProjectTasksComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private taskService = inject(ProjectTaskService);
  private yearMonthFilter = inject(YearMonthFilterService);
  private messageService = inject(MessageService);
  readonly pageHeaderService = inject(PageHeaderSearchActionService);

  groups: MyProjectTasks[] = [];
  loading = false;
  loadFailed = false;

  /** Driven by the main header's year/month selects, shared with Work Log. */
  selectedYear: number;
  selectedMonth: number;

  /**
   * Which project groups are open. Keyed by project id so the state survives a reload —
   * logging effort refetches, and collapsing everything underneath the user would lose
   * their place.
   */
  expanded = new Set<string>();

  searchText = '';

  constructor() {
    this.selectedYear = this.yearMonthFilter.currentYear;
    this.selectedMonth = this.yearMonthFilter.currentMonth;
  }

  ngOnInit(): void {
    // Deferred by a microtask, deliberately.
    //
    // These write to app-level subjects the main header renders from. Called straight from
    // ngOnInit they change a value the header has already been checked for in this cycle,
    // which Angular reports as NG0100 against the @if around the header's filter button.
    // Letting the cycle finish first makes the change a normal subsequent update.
    //
    // No filter-toggle is requested: year and month live in the header itself, and the
    // in-page filter bar moved out with them, so there is nothing left to show or hide.
    queueMicrotask(() => {
      this.pageHeaderService.clearPageConfig();
      this.pageHeaderService.setPlaceholder('Search project or task…');
    });

    this.pageHeaderService.searchText
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(text => {
        this.searchText = text ?? '';
        this.applyFilter();
      });

    // The header's year/month drive the page. Both emit their current value on subscribe,
    // so guard against reloading for a period we already hold — otherwise the initial
    // emissions would fire two redundant requests before the first paint.
    this.yearMonthFilter.year
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(year => {
        if (year !== this.selectedYear) {
          this.selectedYear = year;
          this.onPeriodChange();
        }
      });

    this.yearMonthFilter.month
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(month => {
        if (month !== this.selectedMonth) {
          this.selectedMonth = month;
          this.onPeriodChange();
        }
      });

    this.load();
  }

  /**
   * Clears the header config on the way out, as every sibling page does — otherwise this
   * page's search placeholder would persist onto whatever is navigated to next.
   */
  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  load(): void {
    this.loading = true;
    this.loadFailed = false;

    this.taskService
      .getMyProjectTasks(this.selectedYear, this.selectedMonth)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          this.loadFailed = true;
          this.messageService.add({
            severity: 'error',
            summary: 'Could not load your tasks',
            detail: err.status === 401
              ? 'Your sign-in is not linked to an employee record.'
              : 'Please try again.',
          });
          return of([] as MyProjectTasks[]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(groups => {
        this.groups = groups ?? [];
        this.applyFilter();
        this.loading = false;

        // A single project opens itself — there is nothing to choose between.
        if (this.groups.length === 1) {
          this.expanded.add(this.groups[0].projectId);
        }
      });
  }

  /**
   * Reacts to the header's period change. Deliberately does NOT write back to the service —
   * the header already set it, and echoing it would re-emit and loop.
   */
  private onPeriodChange(): void {
    // Allocation is per month, so the project list itself changes with the period; an id
    // left open from another month could point at a project no longer listed.
    this.expanded.clear();
    this.load();
  }

  /**
   * Groups matching the search, which filters on project and task names alike.
   *
   * A plain FIELD, recomputed only when the data or the search text changes — deliberately
   * NOT a getter. A getter returning `.filter(...)` hands change detection a new array
   * reference every cycle; here that re-evaluates an *ngFor which mounts child components,
   * so it does not settle. The same fault froze a date picker on the presales tracker.
   */
  filteredGroups: MyProjectTasks[] = [];

  private applyFilter(): void {
    const search = this.searchText.trim().toLowerCase();

    if (!search) {
      this.filteredGroups = this.groups;
      return;
    }

    this.filteredGroups = this.groups.filter(g =>
      g.projectName.toLowerCase().includes(search)
      || (g.projectCode ?? '').toLowerCase().includes(search)
      || (g.clientName ?? '').toLowerCase().includes(search)
      || g.tasks.some(t => t.name.toLowerCase().includes(search)
        || (t.taskNumber ?? '').toLowerCase().includes(search)));
  }

  toggle(projectId: string): void {
    if (this.expanded.has(projectId)) {
      this.expanded.delete(projectId);
    } else {
      this.expanded.add(projectId);
    }
  }

  isExpanded(projectId: string): boolean {
    return this.expanded.has(projectId);
  }

  // ---------- totals for the header ----------

  get totalProjects(): number {
    return this.groups.length;
  }

  get totalTasks(): number {
    return this.groups.reduce((sum, g) => sum + g.taskCount, 0);
  }

  get totalDone(): number {
    return this.groups.reduce((sum, g) => sum + g.doneCount, 0);
  }

  donePercent(group: MyProjectTasks): number {
    return group.taskCount ? Math.round((group.doneCount / group.taskCount) * 100) : 0;
  }
}
